/**
 * Dashboard "Services" panel — feature-discovery strip sitting between
 * the top action grid (Ask + Balance) and the readings list.
 *
 * Each tile is driven by an entry in `SERVICES` — to add a future
 * service (e.g. dasha reading, transit forecast), just append a new
 * config object. Tiles render in a responsive 3-up grid (1-up on
 * mobile, 2-up on tablet).
 *
 * BTR is launched via the existing `RectifyBirthTimeModal` rather than
 * navigating to /app/profile, so a Dashboard tap goes straight into
 * the flow.
 *
 * Each tile optionally surfaces a `contextualBadge()` — a short
 * personalised ribbon when relevant (e.g. "Your birth time is
 * approximate" on the BTR tile when `birth_time_confidence` is
 * approximate / unknown / not_set). When `contextualBadge` returns
 * null the tile renders without the ribbon — purely cosmetic, doesn't
 * affect access.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { useAuth } from '@/lib/auth';
import { fetchRegion, formatPrice } from '@/lib/region';
import RectifyBirthTimeModal from '@/components/RectifyBirthTimeModal';
import { Heart, Clock, Users, ArrowRight, Sparkle } from 'lucide-react';


// ---------------------------------------------------------------------
// Service registry — add a new service by pushing a new entry here.
//
// Pricing for `compatibility` + `btr` is fetched live from
// /api/pricing (Round 47), so the strike-through list price + discount
// badge stay in sync with the admin's pricing dashboard. The expert
// tile still uses a static "From $X" label because expert sessions
// have multiple tiers.
// ---------------------------------------------------------------------
const SERVICES = [
    {
        id: 'compatibility',
        title: 'Compatibility reading',
        subtitle: 'See how your charts work together — dating, engaged, married, or anything in between.',
        priceFromAPI: 'compatibility',
        cta: 'Run a reading',
        icon: Heart,
        action: { type: 'navigate', to: '/app/compatibility' },
        contextualBadge: ({ profiles }) => {
            // If user has a non-self profile (spouse/friend/other), surface them by name.
            const partner = (profiles || []).find(p => p.relation && p.relation !== 'self');
            if (partner) {
                return `Check with ${partner.name}`;
            }
            return null;
        },
    },
    {
        id: 'btr',
        title: 'Birth-time analysis',
        subtitle: "Sharpen every future reading by pinning down your exact birth Lagna using the KP Ruling Planets method.",
        priceFromAPI: 'btr',
        cta: 'Analyse my birth time',
        ctaIfRepeat: 'Refine again',
        icon: Clock,
        action: { type: 'btr-modal' },
        contextualBadge: ({ user }) => {
            const btc = user?.birth_time_confidence;
            // Rectified users still see the tile — they may have new
            // information (a corrected hospital record, a competing
            // life-event fit) and can run a fresh pass any time.
            if (btc === 'rectified') return 'Previously analysed';
            if (['approximate', 'unknown', 'not_set'].includes(btc)) {
                return 'Your birth time is approximate';
            }
            return null;
        },
        ctaPicker: ({ user }) => (
            user?.birth_time_confidence === 'rectified' ? 'ctaIfRepeat' : 'cta'
        ),
    },
    {
        id: 'expert',
        title: 'Expert consultation',
        subtitle: 'Talk one-on-one with a senior astrologer — book a 30-minute, 1-hour, or 1-3 hour session.',
        cost: ({ currency }) => (currency === 'USD' ? 'From $15' : 'From ₹449'),
        cta: 'Browse experts',
        icon: Users,
        action: { type: 'navigate', to: '/app/consult' },
        contextualBadge: () => null,
    },
];


export default function DashboardServicesPanel() {
    const { user, refresh } = useAuth();
    const navigate = useNavigate();
    const [btrOpen, setBtrOpen] = useState(false);
    const [profiles, setProfiles] = useState([]);
    const [currency, setCurrency] = useState('INR');
    // Round 47 — DB-backed pricing keyed by product id. Each value is
    // {list_amount, final_amount, discount_pct, has_discount}.
    const [pricing, setPricing] = useState({});

    // Fetch profiles for the partner-name contextual badge. Fire and
    // forget — the panel renders fine without them.
    useEffect(() => {
        let alive = true;
        api.get('/profiles')
            .then(({ data }) => { if (alive) setProfiles(data.profiles || []); })
            .catch(() => { /* no badge — panel still works */ });
        fetchRegion()
            .then((r) => { if (alive) setCurrency(r?.currency || 'INR'); })
            .catch(() => { /* default INR — tiles still render */ });
        api.get('/pricing')
            .then(({ data }) => {
                if (!alive) return;
                if (data?.currency) setCurrency(data.currency);
                const map = {};
                for (const row of data?.products || []) {
                    map[row.product] = row;
                }
                setPricing(map);
            })
            .catch(() => { /* fallback to static cost() functions */ });
        return () => { alive = false; };
    }, []);

    const ctx = { user, profiles, currency };

    const onAction = (action) => {
        if (action.type === 'navigate') {
            navigate(action.to);
        } else if (action.type === 'btr-modal') {
            setBtrOpen(true);
        }
    };

    // Render the cost cell — strike-through + discount badge for any
    // service whose `priceFromAPI` matches a row from /api/pricing.
    const renderCost = (s) => {
        if (s.priceFromAPI && pricing[s.priceFromAPI]) {
            const p = pricing[s.priceFromAPI];
            const listLabel  = formatPrice(p.list_amount, p.currency);
            const finalLabel = formatPrice(p.final_amount, p.currency);
            if (p.has_discount) {
                return (
                    <span className="inline-flex items-center gap-2">
                        <span className="line-through text-slate-500 text-xs" data-testid={`service-${s.id}-list-price`}>
                            {listLabel}
                        </span>
                        <span className="text-marigold font-semibold" data-testid={`service-${s.id}-final-price`}>
                            {finalLabel}
                        </span>
                        <span
                            className="px-1.5 py-0.5 rounded-sm bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 text-[10px] font-bold uppercase tracking-wider"
                            data-testid={`service-${s.id}-discount-badge`}
                        >
                            {p.discount_pct}% OFF
                        </span>
                    </span>
                );
            }
            return <span data-testid={`service-${s.id}-final-price`}>{finalLabel}</span>;
        }
        // Fallback — static `cost(ctx)` (used by the expert tile).
        return typeof s.cost === 'function' ? s.cost(ctx) : s.cost;
    };

    return (
        <section className="mb-12" data-testid="dashboard-services-panel">
            <div className="flex items-center justify-between mb-5">
                <h2 className="font-serif text-2xl text-parchment flex items-center gap-2">
                    <Sparkle size={18} className="text-marigold" />
                    Explore more
                </h2>
                <span className="hidden sm:inline text-xs text-slate-500 uppercase tracking-wider">
                    Beyond predictions
                </span>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {SERVICES.map((s) => {
                    const Icon = s.icon;
                    const badge = s.contextualBadge ? s.contextualBadge(ctx) : null;
                    // Optional dynamic CTA — services with repeat-use
                    // variants (e.g. BTR: "Analyse my birth time" vs
                    // "Refine again") flip the label via `ctaPicker`.
                    const ctaKey = s.ctaPicker ? s.ctaPicker(ctx) : 'cta';
                    const ctaText = s[ctaKey] || s.cta;
                    return (
                        <Card
                            key={s.id}
                            data-testid={`dashboard-service-${s.id}`}
                            onClick={() => onAction(s.action)}
                            className="relative bg-elevated border-marigold/15 rounded-sm p-6
                                        cursor-pointer transition-all duration-200
                                        hover:border-marigold/50 hover:-translate-y-0.5"
                        >
                            {badge && (
                                <div
                                    className="absolute top-3 right-3 text-[10px] uppercase tracking-wider
                                               bg-marigold/10 text-marigold border border-marigold/30
                                               rounded-sm px-2 py-0.5"
                                    data-testid={`dashboard-service-${s.id}-badge`}
                                >
                                    {badge}
                                </div>
                            )}

                            <Icon className="text-marigold mb-3" size={20} />

                            <h3 className="font-serif text-lg text-parchment mb-2">{s.title}</h3>
                            <p className="text-sm text-slate-400 leading-relaxed mb-4 min-h-[60px]">
                                {s.subtitle}
                            </p>

                            <div className="flex items-center justify-between pt-3 border-t border-marigold/10">
                                <span className="text-xs uppercase tracking-wider text-copper">
                                    {renderCost(s)}
                                </span>
                                <span
                                    className="text-sm font-medium flex items-center gap-1.5 text-marigold"
                                    data-testid={`dashboard-service-${s.id}-cta`}
                                >
                                    {ctaText}
                                    <ArrowRight size={14} />
                                </span>
                            </div>
                        </Card>
                    );
                })}
            </div>

            <RectifyBirthTimeModal
                open={btrOpen}
                onOpenChange={setBtrOpen}
                onSuccess={async () => { await refresh?.(); }}
            />
        </section>
    );
}
